import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import DualDatePicker from '../../components/common/DualDatePicker';
import Card from '../../components/common/Card';
import BeltBadge from '../../components/common/BeltBadge';
import { getMonthlyReport, getAbsentAlerts } from '../../services/attendanceService';
// Revenue/Students/Expenses used to read the legacy Payment model (paymentService.js) — a
// collection nothing writes to any more. Repointed onto the exact same FeePayment-based sources
// every other money screen uses: getFinanceSummary/getLedger (Dashboard, FinanceOverview) and
// getPaymentSummary/getAllPayments (Payments Overview/History) — aliased here since both
// services happen to export a same-named `getPaymentSummary` for two different models.
import { getFinanceSummary, getLedger } from '../../services/financeService';
import { getPaymentSummary as getFeeSummary, getAllPayments } from '../../services/feeService';
import { getStudentReport } from '../../services/studentService';
import { getExpenses, getExpenseSummary } from '../../services/expenseService';
import { downloadExpenseReport, downloadStudentListReport } from '../../services/reportExportService';
import useClubVocab from '../../hooks/useClubVocab';
import {
  MdBarChart, MdDownload, MdPayments, MdPeople, MdCheckCircle,
  MdExpandMore, MdExpandLess, MdWarning, MdReceiptLong, MdTableChart,
  MdEventBusy, MdInbox,
} from 'react-icons/md';
import { useCalendar } from '../../context/CalendarContext';
import { adToBs, bsToAd, getDaysInBsMonth, ktmDayKey } from '../../utils/calendar';
import DualDate from '../../components/common/DualDate';
import { BELT_ORDER } from '../../constants/belts';

const pad2 = (n) => String(n).padStart(2, '0');
const isoLocal = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Kathmandu-local current month "YYYY-MM" (padded local Y/M — no toISOString/UTC).
const THIS_MONTH = (() => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`; })();

// Build the AD date window the report queries should use. A BS month straddles two AD
// months, so when the club calendar is BS we use the TRUE BS-month boundaries (the same
// conversion the Sessions calendar uses): bsToAd(bsY, bsM, 1) … bsToAd(bsY, bsM,
// getDaysInBsMonth(bsY, bsM)). In AD mode we use the literal AD month. ISO strings are
// built from padded local Y/M/D — never toISOString — so there is no UTC drift.
function getReportRange(yearMonth, calendar) {
  const [y, m] = yearMonth.split('-').map(Number);
  if (calendar === 'bs') {
    const bs      = adToBs(new Date(y, m - 1, 15)); // recover the BS month this AD value maps to
    const firstAd = bsToAd(bs.year, bs.month, 1);
    const lastAd  = bsToAd(bs.year, bs.month, getDaysInBsMonth(bs.year, bs.month));
    return { startDate: isoLocal(firstAd), endDate: isoLocal(lastAd) };
  }
  const firstAd = new Date(y, m - 1, 1);
  const lastAd  = new Date(y, m, 0);
  return { startDate: isoLocal(firstAd), endDate: isoLocal(lastAd) };
}

// ── Chart palette ─────────────────────────────────────────────────────────────
// recharts renders inline SVG, so Tailwind tokens / CSS vars don't apply on its color
// props — these are the app's real theme hex values (from index.css), tuned for the
// dark premium look. Data colors (present/late/absent/revenue) read well on both themes.
const CHART = {
  grid:          'rgba(140,140,140,0.18)', // subtle neutral grid, faint on light + dark
  axis:          '#8b949e',                // --color-muted (dark) — medium gray, legible on both
  present:       '#34D399',                // emerald-400
  late:          '#FBBF24',                // amber-400
  absent:        '#F87171',                // red-400
  revenue:       '#EF4444',                // --color-primary
  tooltipBg:     '#161b22',                // --color-card (dark)
  tooltipBorder: '#30363d',                // --color-border (dark)
  tooltipText:   '#E6EDF3',                // near-white — legible on tooltipBg
  tooltipLabel:  '#8b949e',                // --color-muted — small tooltip label
};

// Belt colors for the PieChart + legend — tuned to read on a dark card: the light belts
// (white / pale yellow) are deepened so they aren't glary/washed, and the black dans are
// lifted to slate shades so they stay visible on the dark background instead of vanishing.
const BELT_PIE_COLORS = {
  'White':               '#D1D5DB',
  'White-Yellow Stripe': '#FDE68A',
  'Yellow':              '#FACC15',
  'Yellow-Green Stripe': '#A3E635',
  'Green':               '#4ADE80',
  'Green-Blue Stripe':   '#22D3EE',
  'Blue':                '#60A5FA',
  'Blue-Red Stripe':     '#C084FC',
  'Red':                 '#F87171',
  'Red-Black Stripe':    '#94A3B8',
  'Black - 1st Dan':     '#64748B',
  'Black - 2nd Dan':     '#475569',
  'Black - 3rd Dan':     '#334155',
};

// Semantic data-viz number shades (no text-success/danger tokens exist in this app, so
// these dark-readable shades are used as a controlled exception — see report).
const NUM = {
  positive: 'text-emerald-400',
  negative: 'text-red-400',
  warning:  'text-amber-400',
  neutral:  'text-secondary',
};
// This file previously mixed 50% and 60% as the "at risk" cutoff. Standardised on 60% —
// the named isAtRisk flag (used for the row highlight + warning icon) already used 60%,
// and a martial-arts school is better served flagging a semi-disengaged student earlier
// than missing them.
const ATTENDANCE_GOOD_THRESHOLD    = 80; // at/above this = green
const ATTENDANCE_AT_RISK_THRESHOLD = 60; // below this = red/at-risk; amber in between
const rateColor = (r) => (r == null ? NUM.neutral : r >= ATTENDANCE_GOOD_THRESHOLD ? NUM.positive : r >= ATTENDANCE_AT_RISK_THRESHOLD ? NUM.warning : NUM.negative);

const TAB_DESC = {
  attendance: 'How often your students showed up to class this month.',
  revenue:    "What you collected and what's still owed this month.",
  students:   'A health check on your active students.',
  expenses:   'Money in versus money out this month.',
};

const TABS = [
  ['attendance', 'Attendance', MdCheckCircle],
  ['revenue',    'Revenue',    MdPayments],
  ['students',   'Students',   MdPeople],
  ['expenses',   'Expenses',   MdReceiptLong],
];

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, helper, color = NUM.neutral }) {
  return (
    <div className="bg-card border border-divider rounded-xl p-4">
      <p className="text-xs text-muted">{label}</p>
      <p className={`text-[30px] leading-none font-medium tracking-tight mt-2 ${color}`}>{value}</p>
      {helper && <p className="text-xs text-subtle mt-2 leading-snug">{helper}</p>}
    </div>
  );
}

// ── Calm, helpful empty state ─────────────────────────────────────────────────
function EmptyState({ icon: Icon, title, line }) {
  return (
    <div className="flex flex-col items-center text-center py-14 px-6">
      <Icon className="text-4xl text-subtle mb-3" />
      <p className="text-sm font-medium text-muted">{title}</p>
      <p className="text-xs text-subtle mt-1.5 max-w-xs leading-relaxed">{line}</p>
    </div>
  );
}

// ── Custom chart tooltip ──────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs min-w-[120px]"
      style={{
        background: CHART.tooltipBg,
        border: `1px solid ${CHART.tooltipBorder}`,
        color: CHART.tooltipText,
        boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
      }}
    >
      <p className="mb-1.5" style={{ color: CHART.tooltipLabel }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} className="font-semibold leading-snug" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' && p.name?.includes('NPR')
            ? `NPR ${p.value.toLocaleString()}` : p.value}
        </p>
      ))}
    </div>
  );
}

// ── Section header: title + plain-English description ─────────────────────────
function SectionHeader({ title, description }) {
  return (
    <div className="min-w-0">
      <h2 className="font-bold text-secondary text-base flex items-center gap-2">
        <MdBarChart className="text-secondary flex-shrink-0" /> {title}
      </h2>
      {description && <p className="text-[13px] text-muted mt-1">{description}</p>}
    </div>
  );
}

// ── Export control (lives in the top toolbar; handlers unchanged) ─────────────
function ExportControl({ onPrint, exportMenu }) {
  const [open, setOpen] = useState(false);
  const triggerCls = 'inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full text-sm font-medium bg-card text-secondary border border-divider hover:bg-secondary hover:text-card hover:border-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/30 transition-all duration-200';

  if (exportMenu) {
    return (
      <div className="relative">
        <button type="button" onClick={() => setOpen(o => !o)} className={triggerCls}>
          <MdDownload className="text-base" /> Download <MdExpandMore className="text-base" />
        </button>
        {open && (
          <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-1 w-44 bg-card border border-divider rounded-xl shadow-lg z-50 py-1"
            onMouseLeave={() => setOpen(false)}
          >
            {exportMenu.map(({ label, icon: Icon, onClick, loading }) => (
              <button
                key={label}
                onClick={() => { onClick(); setOpen(false); }}
                disabled={loading}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-secondary hover:bg-nav-hover disabled:opacity-50 transition-colors"
              >
                <Icon className="text-base text-muted flex-shrink-0" />
                {loading ? 'Generating…' : label}
              </button>
            ))}
          </div>
          </>
        )}
      </div>
    );
  }
  return (
    <button type="button" onClick={onPrint} className={triggerCls}>
      <MdDownload className="text-base" /> Export PDF
    </button>
  );
}

// ── Skeleton loader ───────────────────────────────────────────────────────────
function Skeleton({ h = 'h-40' }) {
  return <div className={`${h} bg-nav-hover animate-pulse rounded-2xl`} />;
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    present: 'bg-emerald-500/15 text-emerald-500',
    late:    'bg-amber-500/15 text-amber-500',
    absent:  'bg-red-500/15 text-red-500',
    // FeePayment's real status vocabulary (completed/pending/overdue/partial/waived/
    // proof_submitted) — paid/unpaid are kept too in case any stale cached data still has them.
    paid:      'bg-emerald-500/15 text-emerald-500',
    completed: 'bg-emerald-500/15 text-emerald-500',
    unpaid:    'bg-amber-500/15 text-amber-500',
    pending:   'bg-amber-500/15 text-amber-500',
    partial:   'bg-amber-500/15 text-amber-500',
    proof_submitted: 'bg-blue-500/15 text-blue-500',
    waived:    'bg-nav-hover text-muted',
    failed:    'bg-red-500/15 text-red-500',
    overdue:   'bg-red-500/15 text-red-500',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${map[status] || 'bg-nav-hover text-muted'}`}>
      {status}
    </span>
  );
}

// ── Expandable session row (Attendance tab) ───────────────────────────────────
function SessionRow({ session, formatDate }) {
  const [open, setOpen] = useState(false);
  const present = session.records.filter(r => r.status === 'present').length;
  const late    = session.records.filter(r => r.status === 'late').length;
  const absent  = session.records.filter(r => r.status === 'absent').length;
  const total   = session.records.length;
  const rate    = total ? Math.round(((present + late) / total) * 100) : 0;

  return (
    <>
      <tr
        className="border-b border-divider last:border-0 hover:bg-nav-hover cursor-pointer"
        onClick={() => setOpen(o => !o)}
      >
        <td className="px-4 py-3 text-sm text-secondary font-medium"><DualDate date={session.date} /></td>
        <td className="px-4 py-3 text-sm text-muted">{session.class?.name || '—'}</td>
        <td className="px-4 py-3 text-xs text-subtle hidden md:table-cell">
          {session.class?.trainer?.fullName || '—'}
        </td>
        <td className="px-4 py-3 text-center text-sm text-muted">{total}</td>
        <td className="px-4 py-3 text-center text-sm text-emerald-500 font-medium">{present}</td>
        <td className="px-4 py-3 text-center text-sm text-amber-500 font-medium">{late}</td>
        <td className="px-4 py-3 text-center text-sm text-red-500 font-medium">{absent}</td>
        <td className="px-4 py-3 text-center">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
            rate >= ATTENDANCE_GOOD_THRESHOLD ? 'bg-emerald-500/15 text-emerald-500' :
            rate >= ATTENDANCE_AT_RISK_THRESHOLD ? 'bg-amber-500/15 text-amber-500' : 'bg-red-500/15 text-red-500'
          }`}>{rate}%</span>
        </td>
        <td className="px-3 py-3 text-subtle">
          {open ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
        </td>
      </tr>
      {open && (
        <tr className="bg-nav-hover">
          <td colSpan={9} className="px-6 py-3">
            <div className="flex flex-wrap gap-2">
              {session.records.length === 0
                ? <span className="text-xs text-subtle">No records</span>
                : session.records.map((rec, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-xs bg-card border border-divider rounded-lg px-2.5 py-1 shadow-sm">
                    <span className="text-secondary font-medium">{rec.student?.fullName || 'Unknown'}</span>
                    <StatusBadge status={rec.status} />
                  </span>
                ))
              }
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────────────────────────────────────
function Reports() {
  document.title = 'Reports — TrainAxis';
  const { formatDate, formatMonth, currentCalendar } = useCalendar();
  const v = useClubVocab();
  // Re-word the module-level tab/description strings for gyms/yoga; byte-identical for taekwondo.
  const vz = (str) => str
    .replace(/\bStudents\b/g, v.members).replace(/\bStudent\b/g, v.member)
    .replace(/\bstudents\b/g, v.members.toLowerCase()).replace(/\bstudent\b/g, v.member.toLowerCase());
  const [searchParams, setSearchParams] = useSearchParams();
  const [reportMonth, setReportMonth] = useState(THIS_MONTH);
  const [activeTab, setActiveTab]     = useState(() => {
    const t = searchParams.get('tab');
    return ['attendance', 'revenue', 'expenses', 'students'].includes(t) ? t : 'attendance';
  });
  // Persist the active tab to the URL so it survives a refresh (replace → back button stays sane).
  useEffect(() => {
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('tab', activeTab); return n; }, { replace: true });
  }, [activeTab, setSearchParams]);

  // The AD date window every report query pulls from (BS-month-aware; see getReportRange).
  const range = useMemo(() => getReportRange(reportMonth, currentCalendar), [reportMonth, currentCalendar]);

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: attData, isPending: loadingAtt } = useQuery({
    queryKey: ['report-attendance', range.startDate, range.endDate],
    queryFn:  () => getMonthlyReport({ month: reportMonth, startDate: range.startDate, endDate: range.endDate }).then(r => r.data),
    enabled:  activeTab === 'attendance',
  });

  const { data: alertData } = useQuery({
    queryKey: ['absent-alerts'],
    queryFn:  () => getAbsentAlerts().then(r => r.data),
    enabled:  activeTab === 'attendance',
    staleTime: 1000 * 60 * 5,
  });

  // The BS/AD month+year the finance summary's period label + 6-month trend should anchor
  // to — same "recover the true month" trick getReportRange already uses (adToBs on the 15th),
  // just stopping at the month/year ints instead of continuing to real day boundaries. The
  // CURRENT period's actual income/expenses/net below still use the exact range.startDate/
  // endDate override (financeController.js now accepts one, same precedent as expenseController).
  const financeMonthYear = useMemo(() => {
    const [y, m] = reportMonth.split('-').map(Number);
    if (currentCalendar === 'bs') {
      const bs = adToBs(new Date(y, m - 1, 15));
      return { month: bs.month, year: bs.year };
    }
    return { month: m, year: y };
  }, [reportMonth, currentCalendar]);

  // "Total Revenue"/"Collected this month" + "Net Balance" + "Total Expenses" — the exact same
  // collectRevenueInRange-based source Dashboard and FinanceOverview already use, with the true
  // BS-aware range overriding the computed month window.
  const { data: financeSummaryData, isPending: loadingRev } = useQuery({
    queryKey: ['report-finance-summary', financeMonthYear.month, financeMonthYear.year, currentCalendar, range.startDate, range.endDate],
    queryFn:  () => getFinanceSummary(financeMonthYear.month, financeMonthYear.year, currentCalendar, range.startDate, range.endDate).then(r => r.data),
    // Also fetched on the Expenses tab so "Total Income" / "Net Balance" are correct
    // without first visiting Revenue.
    enabled:  activeTab === 'revenue' || activeTab === 'expenses',
  });

  // "Paid"/"Unpaid"/"Overdue" counts — the same FeePayment status accrual the Payments
  // Overview tile uses (completed / pending+partial / overdue), scoped to this AD fee-month.
  const { data: feeSummaryData } = useQuery({
    queryKey: ['report-fee-summary', reportMonth],
    queryFn:  () => getFeeSummary(reportMonth).then(r => r.data),
    enabled:  activeTab === 'revenue',
  });

  // Daily revenue chart — the same per-day cash-in the Ledger already computes (FeePayment +
  // confirmed OtherIncome, by real paidAt), not a client-side legacy-Payment group-by.
  const { data: ledgerData } = useQuery({
    queryKey: ['report-ledger', range.startDate, range.endDate],
    queryFn:  () => getLedger({ start: range.startDate, end: range.endDate }).then(r => r.data),
    enabled:  activeTab === 'revenue',
  });

  // Payments table — the real FeePayment rows for this fee-month, same source/shape
  // Payments → History already lists.
  const { data: paymentsData, isPending: loadingPayments } = useQuery({
    queryKey: ['report-payments', reportMonth],
    queryFn:  () => getAllPayments({ month: reportMonth, limit: 200 }).then(r => r.data),
    enabled:  activeTab === 'revenue',
  });

  const { data: studentReportData, isPending: loadingStudents } = useQuery({
    queryKey: ['report-students', range.startDate, range.endDate],
    queryFn:  () => getStudentReport({ month: reportMonth, startDate: range.startDate, endDate: range.endDate }).then(r => r.data),
    enabled:  activeTab === 'students',
  });

  const { data: expSummaryData } = useQuery({
    queryKey: ['report-expense-summary', range.startDate, range.endDate],
    queryFn:  () => getExpenseSummary({ startDate: range.startDate, endDate: range.endDate }).then(r => r.data),
    enabled:  activeTab === 'expenses',
  });

  const { data: expListData, isPending: loadingExpenses } = useQuery({
    queryKey: ['report-expenses', range.startDate, range.endDate],
    queryFn:  () => getExpenses({ startDate: range.startDate, endDate: range.endDate, status: 'approved', limit: 100 }).then(r => r.data),
    enabled:  activeTab === 'expenses',
  });

  // ── Derived data ──────────────────────────────────────────────────────────

  // Attendance chart data: one bar group per session (date + class)
  const attChartData = useMemo(() => {
    if (!attData?.sessions) return [];
    return attData.sessions.map(s => {
      const present = s.records.filter(r => r.status === 'present').length;
      const late    = s.records.filter(r => r.status === 'late').length;
      const absent  = s.records.filter(r => r.status === 'absent').length;
      const [yy, mm, dd] = ktmDayKey(s.date).split('-').map(Number); // Kathmandu day
      const bs = adToBs(new Date(yy, mm - 1, dd)); // BS-primary axis label (AD omitted — tick space)
      const label = `${bs.day} ${bs.monthName} ${s.class?.name || ''}`.trim();
      return { label, Present: present, Late: late, Absent: absent };
    });
  }, [attData]);

  // Attendance KPIs
  const attKpi = useMemo(() => {
    const sessions = attData?.sessions || [];
    const totalPresent = sessions.reduce((a, s) => a + s.records.filter(r => r.status === 'present').length, 0);
    const totalLate    = sessions.reduce((a, s) => a + s.records.filter(r => r.status === 'late').length, 0);
    const totalRecords = sessions.reduce((a, s) => a + s.records.length, 0);
    const rate = totalRecords ? Math.round(((totalPresent + totalLate) / totalRecords) * 100) : 0;
    return {
      sessions: sessions.length,
      present:  totalPresent + totalLate,
      rate,
      atRisk:   alertData?.alerts?.length || 0,
    };
  }, [attData, alertData]);

  // Revenue chart: daily revenue line — from the Ledger's own per-day cash-in (FeePayment +
  // confirmed OtherIncome, real paidAt), not a client-side group-by over the legacy Payment list.
  const revChartData = useMemo(() => {
    const daily = ledgerData?.daily || [];
    return daily
      .filter(d => (d.total?.in || 0) > 0)
      .map(d => {
        const [yy, mm, dd] = d.date.split('-').map(Number);
        const bs = adToBs(new Date(yy, mm - 1, dd)); // BS-primary axis label (AD omitted — tick space)
        return { label: `${bs.day} ${bs.monthName}`, 'Revenue (NPR)': d.total.in };
      });
  }, [ledgerData]);

  // Belt distribution for PieChart
  const beltChartData = useMemo(() => {
    const students = studentReportData?.students || [];
    const counts = {};
    for (const s of students) counts[s.currentGrade] = (counts[s.currentGrade] || 0) + 1;
    return BELT_ORDER
      .filter(b => counts[b])
      .map(b => ({ name: b, value: counts[b], color: BELT_PIE_COLORS[b] || '#9CA3AF' }));
  }, [studentReportData]);

  // Student KPIs
  const studentKpi = useMemo(() => {
    const students = studentReportData?.students || [];
    const atRisk   = students.filter(s => s.attendanceRate !== null && s.attendanceRate < ATTENDANCE_AT_RISK_THRESHOLD).length;
    // "Still owing" for this fee-month, in FeePayment's own vocabulary — the legacy model had
    // no concept of partial/proof_submitted; waived/completed are never fee-due.
    const feeDue   = students.filter(s => ['pending', 'overdue', 'partial', 'proof_submitted'].includes(s.feeStatus)).length;
    const rates    = students.filter(s => s.attendanceRate !== null).map(s => s.attendanceRate);
    const avgRate  = rates.length ? Math.round(rates.reduce((a, b) => a + b, 0) / rates.length) : null;
    return { total: students.length, atRisk, feeDue, avgRate };
  }, [studentReportData]);

  const [expExporting, setExpExporting]   = useState(false);
  const [studExporting, setStudExporting] = useState(false);

  const monthToDateRange = (month) => {
    const [year, mon] = month.split('-').map(Number);
    const start = new Date(year, mon - 1, 1);
    const end   = new Date(year, mon, 0);
    return {
      startDate: start.toISOString().slice(0, 10),
      endDate  : end.toISOString().slice(0, 10),
    };
  };

  const handlePrint = () => window.print();

  // Active tab's export control config (handlers unchanged — just relocated to the toolbar).
  const exportProps = activeTab === 'students'
    ? { exportMenu: [
        { label: 'Download PDF', icon: MdDownload,   loading: studExporting, onClick: () => downloadStudentListReport({ format: 'pdf' }, setStudExporting) },
        { label: 'Export CSV',   icon: MdTableChart, loading: studExporting, onClick: () => downloadStudentListReport({ format: 'csv' }, setStudExporting) },
      ] }
    : activeTab === 'expenses'
    ? { exportMenu: [
        { label: 'Download PDF',     icon: MdDownload,   loading: expExporting, onClick: () => downloadExpenseReport({ format: 'pdf', ...monthToDateRange(reportMonth), status: 'approved' }, setExpExporting) },
        { label: 'Export CSV (All)', icon: MdTableChart, loading: expExporting, onClick: () => downloadExpenseReport({ format: 'csv', ...monthToDateRange(reportMonth) }, setExpExporting) },
      ] }
    : { onPrint: handlePrint };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Header toolbar */}
      <div>
        {/* ROW 1 — period selector (left) + export (right) */}
        <div className="flex items-center gap-3">
          <DualDatePicker monthOnly value={reportMonth} onChange={setReportMonth} />
          <div className="ml-auto flex-shrink-0">
            <ExportControl {...exportProps} />
          </div>
        </div>

        {/* ROW 2 — unified, full-width segmented tab bar */}
        <div className="flex w-full border-b border-divider gap-1 mt-4">
          {TABS.map(([val, label, Icon]) => {
            const active = activeTab === val;
            return (
              <button
                key={val}
                onClick={() => setActiveTab(val)}
                className={`flex-1 flex items-center justify-center gap-1.5 h-10 -mb-px text-sm font-medium border-b-2 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/30 ${
                  active
                    ? 'border-secondary text-secondary'
                    : 'border-transparent text-muted hover:text-secondary'
                }`}
              >
                <Icon className={active ? 'text-secondary' : 'text-muted'} /> {vz(label)}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── ATTENDANCE ─────────────────────────────────────────────────── */}
      {activeTab === 'attendance' && (
        <div className="space-y-5">
          <SectionHeader
            title={`Attendance Report — ${formatMonth(reportMonth)}`}
            description={vz(TAB_DESC.attendance)}
          />

          {loadingAtt ? (
            <Skeleton h="h-32" />
          ) : (
            <>
              {/* KPI cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <KpiCard label="Total Sessions" value={attKpi.sessions} helper="Classes you held this month" />
                <KpiCard label="Total Present"  value={attKpi.present} color={NUM.positive} helper={`${v.member} check-ins across all classes`} />
                <KpiCard label="Avg Attendance" value={attKpi.rate !== null ? `${attKpi.rate}%` : '—'}
                         color={rateColor(attKpi.rate)} helper={`Of ${v.members.toLowerCase()} who were expected`} />
                <KpiCard label={`At-Risk ${v.members}`} value={attKpi.atRisk}
                         color={attKpi.atRisk > 0 ? NUM.warning : NUM.neutral} helper="Missed 3+ classes in a row" />
              </div>

              {/* Bar chart */}
              {attChartData.length > 0 && (
                <Card>
                  <p className="text-xs text-muted mb-3 font-medium">Sessions this month</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={attChartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                      <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: CHART.axis }} />
                      <YAxis axisLine={false} tickLine={false} allowDecimals={false} tick={{ fontSize: 11, fill: CHART.axis }} />
                      <ChartTooltip cursor={{ fill: CHART.grid }} content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11, color: CHART.axis }} />
                      <Bar dataKey="Present" stackId="a" fill={CHART.present} maxBarSize={30} />
                      <Bar dataKey="Late"    stackId="a" fill={CHART.late}    maxBarSize={30} />
                      <Bar dataKey="Absent"  stackId="a" fill={CHART.absent}  maxBarSize={30} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}

              {/* Absence alerts */}
              {alertData?.alerts?.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                  <p className="text-sm font-semibold text-amber-500 mb-2 flex items-center gap-1.5">
                    <MdWarning /> Absence Alerts
                  </p>
                  <div className="space-y-1.5">
                    {alertData.alerts.map(a => (
                      <div key={a.student._id} className="flex items-center justify-between text-xs text-muted">
                        <span>{a.student.user?.name || a.student.fullName}</span>
                        <span className="font-semibold text-amber-500">{a.consecutiveAbsences} consecutive absences</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sessions table */}
              {!attData?.sessions?.length ? (
                <Card>
                  <EmptyState
                    icon={MdEventBusy}
                    title="No sessions yet for this month"
                    line="Once you take attendance in a class, it'll show up here."
                  />
                </Card>
              ) : (
                <Card className="p-0 overflow-hidden">
                  {/* Mobile cards (<lg) — rendered from the same attData.sessions array */}
                  <div className="lg:hidden divide-y divide-divider">
                    {attData.sessions.map((s, i) => {
                      const present = s.records.filter(r => r.status === 'present').length;
                      const late    = s.records.filter(r => r.status === 'late').length;
                      const absent  = s.records.filter(r => r.status === 'absent').length;
                      const total   = s.records.length;
                      const rate    = total ? Math.round(((present + late) / total) * 100) : 0;
                      return (
                        <div key={i} className="p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-secondary"><DualDate date={s.date} /></p>
                              <p className="text-sm text-muted truncate">{s.class?.name || '—'}</p>
                            </div>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                              rate >= ATTENDANCE_GOOD_THRESHOLD ? 'bg-emerald-500/15 text-emerald-500' :
                              rate >= ATTENDANCE_AT_RISK_THRESHOLD ? 'bg-amber-500/15 text-amber-500' : 'bg-red-500/15 text-red-500'
                            }`}>{rate}%</span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3 text-sm">
                            <div className="flex justify-between gap-2"><span className="text-muted">Trainer</span><span className="text-secondary font-medium truncate">{s.class?.trainer?.fullName || '—'}</span></div>
                            <div className="flex justify-between gap-2"><span className="text-muted">Total</span><span className="text-secondary font-medium">{total}</span></div>
                            <div className="flex justify-between gap-2"><span className="text-muted">Present</span><span className="text-emerald-500 font-medium">{present}</span></div>
                            <div className="flex justify-between gap-2"><span className="text-muted">Late</span><span className="text-amber-500 font-medium">{late}</span></div>
                            <div className="flex justify-between gap-2"><span className="text-muted">Absent</span><span className="text-red-500 font-medium">{absent}</span></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Desktop table (lg+) */}
                  <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-nav-hover text-xs text-muted font-medium border-b border-divider">
                        <th className="text-left px-4 py-3">Date</th>
                        <th className="text-left px-4 py-3">Class</th>
                        <th className="text-left px-4 py-3 hidden md:table-cell">Trainer</th>
                        <th className="text-center px-4 py-3">Total</th>
                        <th className="text-center px-4 py-3 text-emerald-500">Present</th>
                        <th className="text-center px-4 py-3 text-amber-500">Late</th>
                        <th className="text-center px-4 py-3 text-red-500">Absent</th>
                        <th className="text-center px-4 py-3">Rate</th>
                        <th className="px-3 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {attData.sessions.map((s, i) => (
                        <SessionRow key={i} session={s} formatDate={formatDate} />
                      ))}
                    </tbody>
                  </table>
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* ── REVENUE ────────────────────────────────────────────────────── */}
      {activeTab === 'revenue' && (
        <div className="space-y-5">
          <SectionHeader
            title={`Revenue Report — ${formatMonth(reportMonth)}`}
            description={TAB_DESC.revenue}
          />

          {loadingRev ? (
            <Skeleton h="h-32" />
          ) : (
            <>
              {/* KPI cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <KpiCard
                  label="Total Revenue"
                  value={`NPR ${(financeSummaryData?.income?.total || 0).toLocaleString()}`}
                  color={NUM.positive}
                  helper="Collected this month"
                />
                <KpiCard label="Paid"    value={feeSummaryData?.summary?.completed || 0} color={NUM.positive} helper={`${v.members} who've paid`} />
                <KpiCard label="Unpaid"  value={feeSummaryData?.summary?.pending   || 0} color={NUM.warning}  helper={`${v.members} who haven't paid yet`} />
                <KpiCard label="Overdue" value={feeSummaryData?.summary?.overdue   || 0} color={NUM.negative} helper="Past their due date" />
              </div>

              {/* Daily revenue line chart */}
              {revChartData.length > 0 ? (
                <Card>
                  <p className="text-xs text-muted mb-3 font-medium">Daily collections (paid payments)</p>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={revChartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                      <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: CHART.axis }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: CHART.axis }} />
                      <ChartTooltip cursor={{ stroke: CHART.grid }} content={<CustomTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="Revenue (NPR)"
                        stroke={CHART.revenue}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4, fill: CHART.revenue, stroke: CHART.tooltipBg, strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              ) : (
                <Card>
                  <EmptyState
                    icon={MdPayments}
                    title="No collections yet"
                    line="Paid fees will show up here as you record them this month."
                  />
                </Card>
              )}

              {/* Payments table */}
              {loadingPayments ? (
                <Skeleton />
              ) : !paymentsData?.payments?.length ? (
                <Card>
                  <EmptyState
                    icon={MdInbox}
                    title="No payments this month"
                    line="Fees you record for this month will appear here."
                  />
                </Card>
              ) : (
                <Card className="p-0 overflow-hidden">
                  <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-nav-hover text-xs text-muted font-medium border-b border-divider">
                        <th className="text-left px-5 py-3">{v.member}</th>
                        <th className="text-left px-4 py-3">Amount</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-left px-4 py-3 hidden md:table-cell">Method</th>
                        <th className="text-left px-4 py-3 hidden md:table-cell">Paid Date</th>
                        <th className="text-left px-4 py-3 hidden lg:table-cell">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentsData.payments.map(p => (
                        <tr
                          key={p._id}
                          className={`border-b border-divider last:border-0 ${
                            p.status === 'overdue' ? 'bg-red-500/10' : ''
                          }`}
                        >
                          <td className="px-5 py-3">
                            <p className="text-sm font-medium text-secondary">{p.studentId?.name || '—'}</p>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-secondary">NPR {((p.totalAmount ?? p.amount) || 0).toLocaleString()}</td>
                          <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                          <td className="px-4 py-3 text-xs text-muted hidden md:table-cell capitalize">
                            {p.method || '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted hidden md:table-cell">
                            {(p.paidAt || p.paidDate) ? <DualDate date={p.paidAt || p.paidDate} /> : "—"}
                          </td>
                          <td className="px-4 py-3 text-xs text-subtle hidden lg:table-cell">
                            {p.notes || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* ── STUDENTS ───────────────────────────────────────────────────── */}
      {activeTab === 'students' && (
        <div className="space-y-5">
          <SectionHeader
            title={`${v.member} Progress — ${formatMonth(reportMonth)}`}
            description={vz(TAB_DESC.students)}
          />

          {loadingStudents ? (
            <Skeleton h="h-32" />
          ) : (
            <>
              {/* KPI cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <KpiCard label="Total Active" value={studentKpi.total} helper={`Currently enrolled ${v.members.toLowerCase()}`} />
                <KpiCard
                  label="At-Risk"
                  value={studentKpi.atRisk}
                  helper="Low attendance (under 60%)"
                  color={studentKpi.atRisk > 0 ? NUM.warning : NUM.neutral}
                />
                <KpiCard
                  label="Fee Due"
                  value={studentKpi.feeDue}
                  helper="Haven't paid this month"
                  color={studentKpi.feeDue > 0 ? NUM.negative : NUM.neutral}
                />
                <KpiCard
                  label="Avg Attendance"
                  value={studentKpi.avgRate !== null ? `${studentKpi.avgRate}%` : '—'}
                  helper={`Across all active ${v.members.toLowerCase()}`}
                  color={rateColor(studentKpi.avgRate)}
                />
              </div>

              {/* Belt distribution pie chart */}
              {beltChartData.length > 0 && (
                <Card>
                  <p className="text-xs text-muted mb-3 font-medium">Belt distribution</p>
                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    <ResponsiveContainer width={220} height={220}>
                      <PieChart>
                        <Pie
                          data={beltChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          innerRadius={40}
                        >
                          {beltChartData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} stroke={CHART.tooltipBg} strokeWidth={2} />
                          ))}
                        </Pie>
                        <ChartTooltip
                          formatter={(val, name) => [`${val} ${v.members.toLowerCase()}`, name]}
                          contentStyle={{ background: CHART.tooltipBg, border: `1px solid ${CHART.tooltipBorder}`, borderRadius: 8, fontSize: 12 }}
                          itemStyle={{ color: CHART.tooltipText }}
                          labelStyle={{ color: CHART.tooltipLabel }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap gap-x-6 gap-y-2">
                      {beltChartData.map(b => (
                        <div key={b.name} className="flex items-center gap-2 text-xs text-muted">
                          <span
                            className="w-3 h-3 rounded-full flex-shrink-0 border border-divider"
                            style={{ background: b.color }}
                          />
                          <span>{b.name}</span>
                          <span className="font-semibold text-secondary">{b.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* Students table */}
              {!studentReportData?.students?.length ? (
                <Card>
                  <EmptyState
                    icon={MdPeople}
                    title={`No active ${v.members.toLowerCase()}`}
                    line={`Add ${v.members.toLowerCase()} to your club and their progress will appear here.`}
                  />
                </Card>
              ) : (
                <Card className="p-0 overflow-hidden">
                  <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-nav-hover text-xs text-muted font-medium border-b border-divider">
                        <th className="text-left px-5 py-3">{v.member}</th>
                        <th className="text-left px-4 py-3">Belt</th>
                        <th className="text-left px-4 py-3">Attendance</th>
                        <th className="text-left px-4 py-3 hidden md:table-cell">Last Seen</th>
                        <th className="text-left px-4 py-3">Fee Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {studentReportData.students.map(s => {
                        const isAtRisk = s.attendanceRate !== null && s.attendanceRate < ATTENDANCE_AT_RISK_THRESHOLD;
                        return (
                          <tr
                            key={s._id}
                            className={`border-b border-divider last:border-0 ${
                              isAtRisk ? 'bg-amber-500/10' : 'hover:bg-nav-hover'
                            }`}
                          >
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-2">
                                {isAtRisk && <MdWarning className="text-amber-500 flex-shrink-0" size={14} />}
                                <div>
                                  <p className="text-sm font-medium text-secondary">
                                    {s.user?.name || s.fullName}
                                  </p>
                                  <p className="text-xs text-subtle">{s.user?.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3"><BeltBadge belt={s.currentGrade} size="xs" /></td>
                            <td className="px-4 py-3">
                              {s.totalSessionsThisMonth === 0 ? (
                                <span className="text-xs text-subtle">No sessions</span>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                    s.attendanceRate >= ATTENDANCE_GOOD_THRESHOLD ? 'bg-emerald-500/15 text-emerald-500' :
                                    s.attendanceRate >= ATTENDANCE_AT_RISK_THRESHOLD ? 'bg-amber-500/15 text-amber-500' :
                                    'bg-red-500/15 text-red-500'
                                  }`}>
                                    {s.attendanceRate}%
                                  </span>
                                  <span className="text-xs text-subtle">
                                    {s.attendanceThisMonth}/{s.totalSessionsThisMonth}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs text-muted hidden md:table-cell">
                              {s.lastAttendanceDate ? <DualDate date={s.lastAttendanceDate} /> : "—"}
                            </td>
                            <td className="px-4 py-3">
                              {s.feeStatus
                                ? <StatusBadge status={s.feeStatus} />
                                : <span className="text-xs text-subtle">No record</span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* ── EXPENSES ────────────────────────────────────────────────────── */}
      {activeTab === 'expenses' && (
        <div className="space-y-5">
          <SectionHeader
            title={`Expense Report — ${formatMonth(reportMonth)}`}
            description={TAB_DESC.expenses}
          />

          {/* KPI cards */}
          {expSummaryData?.summary && (() => {
            const s = expSummaryData.summary;
            // Same formula AND source Dashboard uses: collectRevenueInRange minus approved
            // expenses for the period — both inputs already match Dashboard's own definitions
            // (financeSummaryData.income.total; s.thisMonthTotal is itself the same
            // status:'approved' Expense aggregate Dashboard's approvedExpenses uses).
            const totalIncome   = financeSummaryData?.income?.total || 0;
            const totalExpenses = s.thisMonthTotal || 0;
            const netBalance    = totalIncome - totalExpenses;
            return (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard label="Total Income"     value={`NPR ${totalIncome.toLocaleString()}`}   color={NUM.positive} helper="Money in this month" />
                <KpiCard label="Total Expenses"   value={`NPR ${totalExpenses.toLocaleString()}`} color={NUM.negative} helper="Money out this month" />
                <KpiCard label="Net Balance"      value={`NPR ${Math.abs(netBalance).toLocaleString()}`}
                         color={netBalance >= 0 ? NUM.positive : NUM.negative} helper="Income minus expenses" />
                <KpiCard label="Pending Approval" value={s.pendingCount} color={NUM.warning} helper="Expenses awaiting sign-off" />
              </div>
            );
          })()}

          {/* Category breakdown */}
          {expSummaryData?.summary?.categoryBreakdown?.length > 0 && (
            <Card>
              <h3 className="font-semibold text-secondary mb-4">Category Breakdown</h3>
              <div className="space-y-2">
                {expSummaryData.summary.categoryBreakdown.map(c => {
                  const total = expSummaryData.summary.thisMonthTotal || 1;
                  const pct   = Math.round((c.total / total) * 100);
                  return (
                    <div key={c._id}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="capitalize text-muted">{c._id?.replace(/_/g, ' ')}</span>
                        <span className="font-semibold text-secondary">NPR {c.total.toLocaleString()} ({pct}%)</span>
                      </div>
                      <div className="w-full h-2 bg-nav-hover rounded-full">
                        <div className="h-full bg-secondary rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Expense table */}
          <Card>
            <h3 className="font-semibold text-secondary mb-4">Approved Expenses</h3>
            {loadingExpenses ? (
              <Skeleton />
            ) : (expListData?.expenses || []).length === 0 ? (
              <EmptyState
                icon={MdReceiptLong}
                title="No approved expenses"
                line="Approved expenses for this month will be listed here."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-divider text-xs text-muted uppercase tracking-wide">
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Title</th>
                      <th className="px-4 py-3 text-left">Category</th>
                      <th className="px-4 py-3 text-left">Method</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(expListData?.expenses || []).map(exp => (
                      <tr key={exp._id} className="border-b border-divider last:border-0">
                        <td className="px-4 py-3 text-muted"><DualDate date={exp.expenseDate} /></td>
                        <td className="px-4 py-3 text-secondary font-medium">{exp.title}</td>
                        <td className="px-4 py-3 text-muted capitalize">{exp.category?.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-muted capitalize hidden md:table-cell">{exp.paymentMethod?.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3 text-right font-semibold text-secondary">
                          NPR {Number(exp.amount).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-divider">
                      <td colSpan={4} className="px-4 py-3 text-sm font-semibold text-muted">Total</td>
                      <td className="px-4 py-3 text-right font-bold text-secondary">
                        NPR {(expListData?.expenses || [])
                          .reduce((a, e) => a + (e.amount || 0), 0)
                          .toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

export default Reports;
